import { useEffect } from "react";

import { styled } from "@mui/material";
import Stack from "@mui/material/Stack";
import { CharacterCountView } from "@src/components/RichTextEditor/CharacterCountView";
import { FontSize } from "@src/components/RichTextEditor/Extensions/FontSize";
import { MenuBar } from "@src/components/RichTextEditor/MenuBar";
import { useRichEditorState } from "@src/components/RichTextEditor/Util/useRichEditorState";
import { stripHTML } from "@src/utils/stripHTML";
import { CharacterCount } from "@tiptap/extension-character-count";
import { Color } from "@tiptap/extension-color";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

interface RichTextEditorProps {
  value?: string;
  stripped?: string;
  placeholder?: string;
  count?: number;
  limit?: number;
  onChange?: (value: string) => void;
  disabled: boolean;
  error?: boolean;
  minHeight?: string;
  supportToolbar?: boolean;
  editorAttributes?: Record<string, string>;
}

export const RichTextEditor = ({
  value,
  stripped,
  limit,
  placeholder,
  onChange,
  disabled,
  error = false,
  minHeight,
  supportToolbar = false,
  editorAttributes,
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: { HTMLAttributes: { class: "ig-rte" } },
        underline: false,
      }),
      Underline,
      TextStyle,
      Color,
      FontSize,
      CharacterCount.configure({
        limit,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    editorProps: { attributes: editorAttributes },
    editable: !disabled,
    content: value,
    onUpdate: (props) => {
      const raw = props.editor.getHTML();
      const stripped = stripHTML(raw);
      onChange?.(stripped ? raw : "");
    },
  });
  useEffect(() => {
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);
  const state = useRichEditorState(editor);

  if (editor === null) {
    return null;
  }

  return (
    <StyledStack
      direction="column"
      alignItems="stretch"
      sx={
        supportToolbar
          ? (theme) => ({
              borderRadius: 1,
              ...(error && { borderColor: theme.palette.error.main }),
              transition: "border-color 150ms, box-shadow 150ms",
              ...(!disabled && {
                "&:hover": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
                },
                "&:focus-within": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${error ? theme.palette.error.main : theme.palette.primary.main}`,
                },
              }),
              "& .tiptap": {
                minHeight: minHeight ?? "160px",
                boxSizing: "border-box",
                cursor: disabled ? "default" : "text",
                color: theme.palette.text.primary,
              },
              "& .tiptap h1": theme.typography.h1,
              "& .tiptap h2": theme.typography.h2,
              "& .tiptap h3": theme.typography.h3,
              "& .tiptap h4, & .tiptap h5, & .tiptap h6": {
                fontWeight: theme.typography.fontWeightBold,
              },
              "& .tiptap ul": {
                listStyleType: "disc",
                paddingLeft: "24px",
                margin: "8px 0",
              },
              "& .tiptap ol": {
                listStyleType: "decimal",
                paddingLeft: "24px",
                margin: "8px 0",
              },
              "& .tiptap li p": { margin: 0 },
              "& .tiptap code": {
                backgroundColor: theme.palette.action.hover,
                borderRadius: "3px",
                padding: "2px 4px",
                fontFamily: "monospace",
              },
              "& .tiptap pre": {
                backgroundColor: theme.palette.action.hover,
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "4px",
                padding: "12px",
                margin: "8px 0",
                whiteSpace: "pre-wrap",
              },
              "& .tiptap pre code": { background: "none", padding: 0 },
              "& .tiptap blockquote": {
                borderLeft: `3px solid ${theme.palette.divider}`,
                paddingLeft: "12px",
                margin: "8px 0",
              },
              "& .tiptap a": {
                color: theme.palette.primary.main,
                textDecoration: "underline",
              },
              "& .tiptap p.is-editor-empty:first-child::before": {
                content: "attr(data-placeholder)",
                color: theme.palette.text.primary,
                opacity: theme.palette.mode === "light" ? 0.42 : 0.5,
                float: "left",
                height: 0,
                pointerEvents: "none",
              },
            })
          : undefined
      }
    >
      <MenuBar
        editor={editor}
        state={state}
        disabled={disabled}
        supportToolbar={supportToolbar}
      />
      <EditorContent
        editor={editor}
        placeholder={placeholder}
        onClick={() => {
          if (supportToolbar && !disabled) {
            editor.commands.focus();
          }
        }}
        style={minHeight ? { minHeight } : undefined}
      />
      <CharacterCountView stripped={stripped} limit={limit} />
    </StyledStack>
  );
};

const StyledStack = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.igBorders.core.default}`,
  "& .tiptap": {
    fontSize: "14px",
    padding: "10px",
    "&.ProseMirror-focused": {
      outline: "none",
    },
    "& p": {
      margin: "0px",
    },
  },
}));
